import {
  AppShell,
  Box,
  Burger,
  Button,
  Card,
  Divider,
  Group,
  HoverCard,
  NavLink,
  PasswordInput,
  Stack,
  Skeleton,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconGauge, IconRefresh, IconSettings, IconUsers } from "@tabler/icons-react";
import { lazy, Suspense, type FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { adminApi } from "./api";
import type {
  AdminAuthResult,
  AdminAuthStatus,
  AdminConnection,
  AdminUser,
  UserDetailData,
} from "./types";

const OverviewPage = lazy(() => import("./pages/OverviewPage").then((module) => ({ default: module.OverviewPage })));
const UsersPage = lazy(() => import("./pages/UsersPage").then((module) => ({ default: module.UsersPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then((module) => ({ default: module.SettingsPage })));
const UserDetailDrawer = lazy(() => import("./components/UserDetailDrawer").then((module) => ({ default: module.UserDetailDrawer })));

type Page = "overview" | "users" | "settings";
const SESSION_STORAGE = "cyberdash.admin.session";
const URL_STORAGE = "cyberdash.admin.url";
const DEFAULT_WORKER_URL = import.meta.env.DEV
  ? "http://localhost:8787"
  : "https://cyberdash.deskbtm.com";

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <Group gap={compact ? 10 : 14} wrap="nowrap" className="brandLockup">
      <span
        className={compact ? "brandSymbol brandSymbolCompact" : "brandSymbol"}
        role="img"
        aria-label="CyberDash"
      />
      <div>
        <Text className={compact ? "brandName brandNameCompact" : "brandName"}>CyberDash</Text>
        {!compact && <Text className="brandDescriptor">Dashboard</Text>}
      </div>
    </Group>
  );
}

function PageSkeleton() {
  return (
    <Stack gap="lg" py="md">
      <Skeleton h={34} w={220} radius="xs" />
      <Skeleton h={92} radius="xs" />
      <Skeleton h={320} radius="xs" />
    </Stack>
  );
}

function normalizeBaseUrl(value: string): string {
  const url = new URL(value.trim());
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    throw new Error("Worker 地址必须使用 http 或 https");
  }
  return url.toString().replace(/\/$/, "");
}

function initialConnection(): AdminConnection | null {
  const raw = sessionStorage.getItem(SESSION_STORAGE);
  if (!raw) return null;
  try {
    const connection = JSON.parse(raw) as AdminConnection;
    if (
      !connection.baseUrl ||
      !connection.sessionToken ||
      !connection.admin?.email ||
      new Date(connection.expiresAt).getTime() <= Date.now()
    ) {
      sessionStorage.removeItem(SESSION_STORAGE);
      return null;
    }
    return connection;
  } catch {
    sessionStorage.removeItem(SESSION_STORAGE);
    return null;
  }
}

function AuthScreen({
  onAuthenticated,
}: {
  onAuthenticated: (baseUrl: string, result: AdminAuthResult) => void;
}) {
  const [baseUrl, setBaseUrl] = useState(localStorage.getItem(URL_STORAGE) ?? DEFAULT_WORKER_URL);
  const [status, setStatus] = useState<AdminAuthStatus | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [bootstrapKey, setBootstrapKey] = useState("");
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkStatus = useCallback(async () => {
    setChecking(true);
    setError(null);
    try {
      const normalized = normalizeBaseUrl(baseUrl);
      const next = await adminApi.authStatus(normalized);
      localStorage.setItem(URL_STORAGE, normalized);
      setBaseUrl(normalized);
      setStatus(next);
    } catch (reason) {
      setStatus(null);
      setError(reason instanceof Error ? reason.message : "无法连接 Worker");
    } finally {
      setChecking(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    void checkStatus();
    // 首次进入只检查一次，之后由“重新检查”按钮控制，避免输入地址时连续请求。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!status) {
      await checkStatus();
      return;
    }
    if (!status.initialized && password !== confirmPassword) {
      setError("两次输入的密码不一致");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const normalized = normalizeBaseUrl(baseUrl);
      const result = status.initialized
        ? await adminApi.login(normalized, { email: email.trim(), password })
        : await adminApi.setup(normalized, bootstrapKey.trim(), {
          displayName: displayName.trim(),
          email: email.trim(),
          password,
        });
      onAuthenticated(normalized, result);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "登录失败");
      if (typeof reason === "object" && reason && "status" in reason && reason.status === 409) {
        void checkStatus();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box className="connectScreen">
      <section className="connectEditorial" aria-label="CyberDash 品牌介绍">
        <Brand />
        <div className="connectStatement">
          <Text className="connectKicker">车辆与用户管理</Text>
          <Title order={1}>
            <span className="connectTitleLine">每次连接，</span>
            <span className="connectTitleLine">清清楚楚。</span>
          </Title>
          <Text>车辆、账号与权益，在同一个安静的视窗里。</Text>
        </div>
        <Text className="connectFootnote">CyberDash Console</Text>
      </section>
      <Card className="connectCard" radius="xs" padding="xl" withBorder>
        <Brand compact />
        <Title order={2} mt={42}>{status?.initialized ? "管理员登录" : "初始化管理账号"}</Title>
        <Text c="dimmed" mt={8} size="sm">
          {status?.initialized
            ? `登录 ${status.app.name} 管理台。`
            : "首次使用需要创建管理员。引导密钥只用于本次验证。"}
        </Text>
        <form onSubmit={submit} className="connectForm">
          <Stack mt="xl" gap="md">
            <TextInput
              label="Worker 地址"
              value={baseUrl}
              onChange={(event) => { setBaseUrl(event.currentTarget.value); setStatus(null); }}
              rightSection={<Button variant="subtle" size="compact-xs" loading={checking} onClick={() => void checkStatus()}>检查</Button>}
              rightSectionWidth={64}
              required
            />
            {status && <Divider label={`${status.app.name} / ${status.app.id}`} labelPosition="center" />}
            {status && !status.initialized && (
              <>
                <TextInput label="管理员姓名" value={displayName} onChange={(event) => setDisplayName(event.currentTarget.value)} maxLength={80} required />
                <PasswordInput
                  label="首次引导密钥"
                  description="Worker 中配置的 ADMIN_API_KEY，仅用于创建首个管理员"
                  value={bootstrapKey}
                  onChange={(event) => setBootstrapKey(event.currentTarget.value)}
                  required
                />
              </>
            )}
            {status && (
              <>
                <TextInput label="邮箱" type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.currentTarget.value)} required />
                <PasswordInput
                  label="密码"
                  description={!status.initialized ? "至少 12 个字符" : undefined}
                  autoComplete={status.initialized ? "current-password" : "new-password"}
                  value={password}
                  onChange={(event) => setPassword(event.currentTarget.value)}
                  minLength={status.initialized ? 1 : 12}
                  maxLength={128}
                  required
                />
              </>
            )}
            {status && !status.initialized && (
              <PasswordInput label="确认密码" autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.currentTarget.value)} minLength={12} maxLength={128} required />
            )}
            {error && <Text c="red" size="sm">{error}</Text>}
            <Button type="submit" loading={submitting || checking} size="md" fullWidth>
              {!status ? "连接 Worker" : status.initialized ? "登录" : "创建管理员并进入"}
            </Button>
          </Stack>
        </form>
      </Card>
    </Box>
  );
}

export default function App() {
  const [connection, setConnection] = useState<AdminConnection | null>(initialConnection);
  const [page, setPage] = useState<Page>("overview");
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] = useDisclosure(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [userDetail, setUserDetail] = useState<UserDetailData | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const clearSession = useCallback(() => {
    sessionStorage.removeItem(SESSION_STORAGE);
    setConnection(null);
    setSelectedUser(null);
  }, []);

  const logout = useCallback(async () => {
    if (connection) {
      try {
        await adminApi.logout(connection);
      } catch {
        // 本地仍然清除会话；过期或断网不应阻止退出。
      }
    }
    clearSession();
  }, [clearSession, connection]);

  const authenticated = (baseUrl: string, result: AdminAuthResult) => {
    const next: AdminConnection = { baseUrl, ...result };
    sessionStorage.setItem(SESSION_STORAGE, JSON.stringify(next));
    localStorage.setItem(URL_STORAGE, baseUrl);
    setConnection(next);
  };

  const openUser = useCallback((user: AdminUser) => {
    if (!connection) return;
    setSelectedUser(user);
    setDetailLoading(true);
    setUserDetail(null);
    adminApi.user(connection, user.id)
      .then(setUserDetail)
      .catch((reason: unknown) => notifications.show({ color: "red", title: "无法加载用户", message: reason instanceof Error ? reason.message : "请求失败" }))
      .finally(() => setDetailLoading(false));
  }, [connection]);

  const changeStatus = useCallback(async (user: AdminUser, status: "active" | "disabled") => {
    if (!connection) return;
    try {
      await adminApi.setUserStatus(connection, user.id, status);
      notifications.show({
        color: "terracotta",
        title: status === "active" ? "账号已恢复" : "账号已停用",
        message: status === "disabled" ? "该 App 的现有会话已全部吊销" : "用户可以重新登录",
      });
      const nextUser = { ...user, status };
      setSelectedUser(nextUser);
      setUserDetail((current) => current ? { ...current, user: { ...current.user, status } } : current);
      setRefreshKey((value) => value + 1);
    } catch (reason) {
      notifications.show({ color: "red", title: "操作失败", message: reason instanceof Error ? reason.message : "请求失败" });
    }
  }, [connection]);

  const navigation = useMemo(() => [
    { key: "overview" as const, label: "概览", icon: IconGauge },
    { key: "users" as const, label: "用户", icon: IconUsers },
    { key: "settings" as const, label: "管理员账号", icon: IconSettings },
  ], []);

  if (!connection) return <AuthScreen onAuthenticated={authenticated} />;

  return (
    <AppShell
      header={{ height: 68 }}
      navbar={{ width: 224, breakpoint: "sm", collapsed: { mobile: !mobileOpened } }}
      padding={{ base: "md", sm: 28 }}
    >
      <AppShell.Header className="appHeader">
        <Group h="100%" px={{ base: "md", sm: 22 }} justify="space-between">
          <Group>
            <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="sm" size="sm" />
            <Brand compact />
          </Group>
          <Group gap="sm">
            <HoverCard width={280} position="bottom-end" shadow="none" openDelay={180} closeDelay={100}>
              <HoverCard.Target>
                <UnstyledButton className="accountTrigger" aria-label="查看当前管理员信息">
                  <span className="accountAvatar">{connection.admin.displayName.trim().charAt(0).toUpperCase() || "A"}</span>
                  <Text size="sm" fw={500} visibleFrom="xs">{connection.admin.displayName}</Text>
                </UnstyledButton>
              </HoverCard.Target>
              <HoverCard.Dropdown className="accountHoverCard">
                <Text size="sm" fw={700}>{connection.admin.displayName}</Text>
                <Text size="sm" c="dimmed" mt={4}>{connection.admin.email}</Text>
                <Divider my="sm" />
                <Text size="xs" c="dimmed">{new URL(connection.baseUrl).host}</Text>
              </HoverCard.Dropdown>
            </HoverCard>
            <Button className="refreshButton" variant="filled" color="ink" leftSection={<IconRefresh size={16} stroke={1.6} />} onClick={() => setRefreshKey((value) => value + 1)}>
              刷新
            </Button>
          </Group>
        </Group>
      </AppShell.Header>
      <AppShell.Navbar p={14} className="appNavbar">
        <AppShell.Section grow>
          <Stack gap={5}>
            {navigation.map((item) => (
              <NavLink
                key={item.key}
                active={page === item.key}
                label={item.label}
                leftSection={<item.icon size={18} stroke={1.45} />}
                onClick={() => { setPage(item.key); closeMobile(); }}
                variant="subtle"
              />
            ))}
          </Stack>
        </AppShell.Section>
      </AppShell.Navbar>
      <AppShell.Main>
        <Suspense fallback={<PageSkeleton />}>
          {page === "overview" && <OverviewPage connection={connection} refreshKey={refreshKey} onUnauthorized={clearSession} onOpenUser={openUser} />}
          {page === "users" && <UsersPage connection={connection} refreshKey={refreshKey} onUnauthorized={clearSession} onOpenUser={openUser} onStatusChange={changeStatus} />}
          {page === "settings" && <SettingsPage connection={connection} onLogout={logout} />}
        </Suspense>
      </AppShell.Main>
      <Suspense fallback={null}>
        <UserDetailDrawer
          opened={selectedUser !== null}
          user={selectedUser}
          detail={userDetail}
          loading={detailLoading}
          onClose={() => setSelectedUser(null)}
          onStatusChange={(status) => selectedUser && changeStatus(selectedUser, status)}
        />
      </Suspense>
    </AppShell>
  );
}
